import { useEffect, useRef } from "react";

export type LiquidMarbleVariant = 'neon' | 'magma' | 'ocean' | 'cotton' | 'sapphire' | 'acid' | 'portal' | 'pool' | string;

interface Props {
  variant?: LiquidMarbleVariant;
}

// ── Vertex shader ─────────────────────────────────────────────────────────────
const VERT_SRC = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// ── Fragment shader ───────────────────────────────────────────────────────────
// Liquid Marble / Fluid distortion with Uniform Colors
const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2  u_resolution;

uniform vec3 u_color1;
uniform vec3 u_color2;
uniform vec3 u_color3;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v -   i + dot(i, C.xx);
  vec2 i1; i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 ));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m ; m = m*m ;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  uv.x *= u_resolution.x / u_resolution.y;

  float t = u_time * 0.15; // Animation speed

  // Distorção de coordenadas baseada no tempo
  vec2 q = vec2(0.);
  q.x = snoise(uv + t * 0.1);
  q.y = snoise(uv + vec2(1.0));
  
  vec2 r = vec2(0.);
  r.x = snoise(uv + 1.2 * q + vec2(1.7, 9.2) + t * 0.15);
  r.y = snoise(uv + 1.2 * q + vec2(8.3, 2.8) + t * 0.126);

  float f = snoise(uv + r * 2.0);

  // Interpolação de cores usando os uniforms recebidos
  vec3 color = mix(u_color3, u_color1, clamp(f * 2.0, 0.0, 1.0));
  color = mix(color, u_color2, clamp(r.x, 0.0, 1.0));
  color = mix(color, u_color3, clamp(q.y * 1.5, 0.0, 1.0));

  // Soft vignette
  vec2 normUv = gl_FragCoord.xy / u_resolution.xy;
  float vig = 1.0 - 0.45 * dot(normUv - 0.5, normUv - 0.5) * 2.5;
  color *= vig;

  
  // Subtle film grain
  float grain = fract(sin(dot(normUv.xy + u_time, vec2(12.9898,78.233))) * 43758.5453);
  color -= grain * 0.04;

  gl_FragColor = vec4(color, 1.0);

}
`;

// ── WebGL helpers ─────────────────────────────────────────────────────────────
function createShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}

function createProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram | null {
  const vs = createShader(gl, gl.VERTEX_SHADER, vert);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, frag);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error("Program link error:", gl.getProgramInfoLog(prog));
    return null;
  }
  return prog;
}

// ── Color Palettes ────────────────────────────────────────────────────────────

const PALETTES: Record<string, { c1: number[], c2: number[], c3: number[] }> = {
  neon:   { c1: [0.0, 0.3, 1.0], c2: [0.1, 1.0, 0.4], c3: [0.02, 0.02, 0.05] }, // Blue & Neon Green
  magma:  { c1: [1.0, 0.2, 0.0], c2: [1.0, 0.6, 0.0], c3: [0.10, 0.01, 0.01] }, // Lava Red & Orange
  ocean:  { c1: [0.0, 0.5, 0.9], c2: [0.0, 0.9, 0.9], c3: [0.0, 0.05, 0.15] },  // Deep Blue & Cyan
  cotton: { c1: [0.9, 0.5, 0.7], c2: [0.5, 0.8, 1.0], c3: [0.98, 0.96, 0.98] }, // Pink & Light Blue on Light
  sapphire: { c1: [0.1, 0.5, 1.0], c2: [0.6, 0.9, 1.0], c3: [0.02, 0.1, 0.3] }, // Electric Blue & Frost
  acid: { c1: [0.6, 0.0, 0.9], c2: [0.0, 1.0, 0.5], c3: [0.05, 0.0, 0.1] }, // Purple & Mint Green
  portal: { c1: [0.4, 0.9, 0.2], c2: [0.12, 0.74, 0.12], c3: [0.03, 0.33, 0.03] }, // Toxic Slime / Portal
  pool:   { c1: [0.88, 0.96, 1.0], c2: [0.30, 0.76, 0.97], c3: [0.01, 0.50, 0.85] }, // Pool Water Caustics
};


// ── Component ─────────────────────────────────────────────────────────────────
export default function WebGLLiquidMarbleBackground({ variant = 'neon' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) return;

    const program = createProgram(gl, VERT_SRC, FRAG_SRC);
    if (!program) return;

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const posLoc  = gl.getAttribLocation(program, "a_position");
    const timeLoc = gl.getUniformLocation(program, "u_time");
    const resLoc  = gl.getUniformLocation(program, "u_resolution");
    
    const locColor1 = gl.getUniformLocation(program, "u_color1");
    const locColor2 = gl.getUniformLocation(program, "u_color2");
    const locColor3 = gl.getUniformLocation(program, "u_color3");

    if (gl.isContextLost()) return;
      gl.useProgram(program);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    let start: number | null = null;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const render = (ts: number) => {
      if (start === null) start = ts;
      const t = (ts - start) / 1000;

      // Extract colors for current variant (fallback to neon if not found)
      const curVariant = variant || 'neon';
      const palette = PALETTES[curVariant] || PALETTES['neon'];

      if (gl.isContextLost()) return;
      gl.useProgram(program); // ensure it's still bound
      gl.uniform3f(locColor1, palette.c1[0], palette.c1[1], palette.c1[2]);
      gl.uniform3f(locColor2, palette.c2[0], palette.c2[1], palette.c2[2]);
      gl.uniform3f(locColor3, palette.c3[0], palette.c3[1], palette.c3[2]);

      gl.uniform1f(timeLoc, t);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.deleteBuffer(buf);
      gl.deleteProgram(program);
    };
  }, [variant]); // Re-run effect or update uniforms when variant changes

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}
